import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import NavigatorManageTable, {
  AvatarNameCell,
  CalendarCell,
  GroupsCell,
  LastActivityCell,
  OverflowCell,
  nameSort,
  SimpleActionsMenu,
} from './NavigatorManageTable';

const BULK_ACTIONS = [
  {
    key: 'deleteCoaches', label: 'Delete', icon: 'delete', iconColor: '#FF0505',
  },
];

const ActionDropdownCell = ({ row }) => {
  const coach = row.original;
  return (
    <SimpleActionsMenu onEdit={() => coach.onEdit(coach)} onDelete={() => coach.onDelete(coach)} />
  );
};

ActionDropdownCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onEdit: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const ManageCoachesTable = ({
  onEditCoach, onDeleteCoach, onResendActivation, onBulkAction, onVisibleCoachesChange,
  canManageCoaches,
}) => {
  const coaches = useSelector((state) => state.navigator.data.coaches);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const allCalendars = useSelector((state) => state.calendars.data.unparsedCalendars);
  const groups = useSelector((state) => state.navigator.data.groups);
  const isLoadingCoaches = useSelector((state) => state.navigator.ui.isLoadingCoaches);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );
  const identifierKey = usernamesEnabled ? 'username' : 'email';

  const searchKeys = useMemo(
    () => ['first', 'last', identifierKey],
    [identifierKey],
  );

  const calendarMap = useMemo(
    () => calendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {}),
    [calendars],
  );

  const allCalendarsMap = useMemo(
    () => allCalendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {}),
    [allCalendars],
  );

  const groupMap = useMemo(
    () => groups.reduce((acc, g) => { acc[g.id] = g; return acc; }, {}),
    [groups],
  );

  const tableData = useMemo(
    () => coaches.map((coach) => {
      let calendarAssignments = [];
      if (coach.calAccess === null) {
        calendarAssignments = [{ id: 'all', name: 'All Calendars' }];
      } else if (Array.isArray(coach.calAccess) && coach.calAccess.length > 0) {
        // Falls back to the archived-inclusive list so it tags rather than drops archived access.
        calendarAssignments = coach.calAccess
          .map((calId) => {
            const cal = calendarMap[calId] || allCalendarsMap[calId];
            if (!cal) return { id: calId, name: 'Archived' };
            return cal.archived
              ? { id: cal.id, name: cal.name, tag: 'Archived' }
              : { id: cal.id, name: cal.name };
          });
      }

      let groupAssignments = [];
      if (coach.userGroupIds === null) {
        groupAssignments = [{ id: 'all', name: 'All Groups' }];
      } else if (Array.isArray(coach.userGroupIds) && coach.userGroupIds.length > 0) {
        groupAssignments = coach.userGroupIds
          .map((gId) => groupMap[gId])
          .filter(Boolean)
          .map((g) => ({ id: g.id, name: g.name }));
      }

      return {
        ...coach,
        calendarAssignments,
        groupAssignments,
        onEdit: onEditCoach,
        onDelete: onDeleteCoach,
        onResendActivation,
      };
    }),
    [
      coaches, calendarMap, allCalendarsMap, groupMap,
      onEditCoach, onDeleteCoach, onResendActivation,
    ],
  );

  const columns = useMemo(() => [
    {
      Header: 'First',
      accessor: 'first',
      sortType: nameSort,
      headerStyle: { width: '240px', padding: '12px 16px 12px 8px' },
      cellStyle: { width: '240px', padding: '6px 16px 6px 8px' },
      Cell: AvatarNameCell,
    },
    {
      Header: 'Last',
      accessor: 'last',
      sortType: nameSort,
      headerStyle: { width: '190px' },
      Cell: OverflowCell,
    },
    {
      Header: usernamesEnabled ? 'Username' : 'Email',
      accessor: usernamesEnabled ? 'username' : 'email',
      headerStyle: { minWidth: '250px', flex: '1' },
      cellStyle: { minWidth: '250px', flex: '1', overflow: 'hidden' },
      Cell: OverflowCell,
    },
    {
      Header: 'Groups',
      id: 'groups',
      accessor: (row) => (row.groupAssignments || []).map((g) => g.name).join(' '),
      headerStyle: { width: '250px', textAlign: 'left' },
      cellStyle: { width: '250px', textAlign: 'left' },
      Cell: GroupsCell,
    },
    {
      Header: 'Calendar',
      id: 'calendar',
      accessor: (row) => (row.calendarAssignments || []).map((c) => c.name).join(' '),
      headerStyle: { width: '200px', textAlign: 'left' },
      cellStyle: { width: '200px', textAlign: 'left' },
      Cell: CalendarCell,
    },
    {
      Header: 'Last Activity',
      id: 'lastActivity',
      accessor: (row) => (row.activated === 1 ? row.lastActivity : 'Invited'),
      disableSortBy: true,
      headerStyle: { width: '150px', textAlign: 'left' },
      cellStyle: { width: '150px', textAlign: 'left' },
      Cell: LastActivityCell,
    },
    ...(canManageCoaches ? [{
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '100px', textAlign: 'center' },
      cellStyle: { width: '100px', textAlign: 'center' },
      Cell: ActionDropdownCell,
    }] : []),
  ], [usernamesEnabled, canManageCoaches]);

  return (
    <NavigatorManageTable
      data={tableData}
      columns={columns}
      isLoading={isLoadingCoaches}
      searchKeys={searchKeys}
      searchPlaceholder='Search for a coach'
      emptyMessage='No coaches found.'
      searchEmptyMessage='No coaches match your search.'
      bulkActions={canManageCoaches ? BULK_ACTIONS : []}
      onBulkAction={onBulkAction}
      entityLabel='coach'
      defaultSortBy={[{ id: 'last', desc: false }]}
      onFilteredDataChange={onVisibleCoachesChange}
    />
  );
};

ManageCoachesTable.propTypes = {
  onEditCoach: PropTypes.func,
  onDeleteCoach: PropTypes.func,
  onResendActivation: PropTypes.func,
  onBulkAction: PropTypes.func.isRequired,
  onVisibleCoachesChange: PropTypes.func,
  canManageCoaches: PropTypes.bool,
};

ManageCoachesTable.defaultProps = {
  onEditCoach: () => {},
  onDeleteCoach: () => {},
  onResendActivation: () => {},
  canManageCoaches: true,
  onVisibleCoachesChange: () => {},
};

export default ManageCoachesTable;
